import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AxiosError, AxiosResponse, AxiosResponseHeaders } from "axios";
import { Thunk } from "../../store";
import {
  BackendClientePorContrato,
  getCurrentDomain,
} from "../../../apis/backend/Api";
import mock_ClientePorContrato from "../../../dataJson/clienteContrato.json";
import { formatearMensajeBackend } from "../../../apis/helper/helperApi";
import { TypeStatus } from "../../helpers/constantes";

interface State {
  itemClientePorContrato: any[];
  status: TypeStatus;
  isLoading: boolean;
  idClientePorContrato: string;
  Success: boolean;
  Mensaje: string | null;
  error: string | null;
}

const initialState: State = {
  itemClientePorContrato: [],
  status: "idle",
  isLoading: false,
  idClientePorContrato: "",
  error: null,
  Success: false,
  Mensaje: null,
};

interface defaultResponse {
  data: any;
  status: number;
  statusText: string;
  headers: AxiosResponseHeaders | {};
  config: any;
  request?: any;
}
const defaultResp: defaultResponse = {
  data: undefined,
  status: 0,
  statusText: "",
  headers: {},
  config: undefined,
};
const itemClientePorContratoSlice = createSlice({
  name: "itemClientePorContrato",
  initialState,
  reducers: {
    setItemClientePorContrato: (state, action: PayloadAction<any[]>) => {
      state.itemClientePorContrato = action.payload;
    },
    getItemClientePorContrato: (state, action: PayloadAction<any[]>) => {
      state.itemClientePorContrato = action.payload;
    },
    setIdClientePorContrato: (state, action: PayloadAction<string>) => {
      state.idClientePorContrato = action.payload;
    },
    setClearItemClientePorContrato: (state, action: PayloadAction<any[]>) => {
      state = initialState;
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setStatus: (state, action: PayloadAction<TypeStatus>) => {
      state.status = action.payload;
    },
    setResult: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setSuccess: (state, action: PayloadAction<boolean>) => {
      state.Success = action.payload;
    },
    setMensaje: (state, action: PayloadAction<string>) => {
      state.Mensaje = action.payload;
    },
  },
});

export const setBackendItemClientePorContrato =
  (tokenBacked: string, IdSesion: number, IdContrato: number): Thunk =>
    async (dispatch): Promise<AxiosResponse | AxiosError> => {
      dispatch(setIsLoading(true));

      try {
        const response: AxiosResponse =
          await BackendClientePorContrato(IdContrato);
        dispatch(setItemClientePorContrato(response.data.Data));
        dispatch(
          setIdClientePorContrato(String(response.data.Data[0].IdCliente)),
        );

        dispatch(setIsLoading(false));
        dispatch(setStatus("succeeded"));

        dispatch(setSuccess(response.data.Success));
        if (response.data.Success === false) {
          dispatch(setIsLoading(false));
          dispatch(setStatus("failed"));
        }

        dispatch(setMensaje(formatearMensajeBackend(response.data.Mensaje)));
        return response.data;
      } catch (error: any) {
        dispatch(setIsLoading(false));
        dispatch(setStatus("failed"));

        if (error.response?.status === 401) {
          dispatch(setStatus("noAutorice"));
          dispatch(
            setMensaje(
              formatearMensajeBackend(
                "Tu sesión ha expirado. Vuelve a iniciar sesión",
              ),
            ),
          );
        } else if (error.response) {
          // Hay respuesta del servidor, pero es otro error (400, 500, etc.)
          console.error(
            "Error de servidor:",
            error.response.status,
            error.response.data,
          );
          dispatch(
            setMensaje(
              `Error ${error.response.status}: Ocurrió un problema en el servidor.`,
            ),
          );
        } else if (error.request) {
          // La solicitud fue enviada, pero no hubo respuesta
          console.error("Error de conexión:", error.message);
          dispatch(
            setMensaje(
              "No se pudo conectar al servidor. Por favor, revisa tu conexión a internet o tu VPN.",
            ),
          );
        } else {
          // Es un error de red (DNS, etc.) o un error de configuración de la solicitud
          console.error("Error de red o solicitud:", error.message);
          dispatch(
            setMensaje(
              "Ocurrió un problema con la red. Por favor, verifica tu conexión.",
            ),
          );
        }
        return error as AxiosError;
      } finally {
        dispatch(setIsLoading(false));
        if (getCurrentDomain() === "localhost") {
          dispatch(setIsLoading(true));
          dispatch(setItemClientePorContrato(mock_ClientePorContrato.Data));
          dispatch(setIsLoading(false));
          dispatch(setStatus("succeeded"));
          dispatch(setSuccess(mock_ClientePorContrato.Success));
          const mock401 = await import("../../../dataJson/401.json");
          if (mock401.status === 401) {
            dispatch(setStatus("noAutorice"));
            dispatch(
              setMensaje(
                formatearMensajeBackend(
                  "Tu sesión ha expirado. Vuelve a iniciar sesión",
                ),
              ),
            );
          }
        }
      }
    };

export const setBackendClearItemClientePorContrato =
  (): Thunk =>
    async (dispatch): Promise<AxiosResponse | AxiosError> => {
      dispatch(setIsLoading(true));
      try {
        let response: AxiosResponse = defaultResp;
        dispatch(setItemClientePorContrato([]));
        dispatch(setIsLoading(false));
        dispatch(setStatus("idle"));
        dispatch(setSuccess(false));
        dispatch(setMensaje(""));
        return response.data;
      } catch (error: any) {
        dispatch(setIsLoading(false));
        dispatch(setStatus("failed"));
        return error as AxiosError;
      } finally {
        dispatch(setIsLoading(false));
      }
    };

export const {
  setItemClientePorContrato,
  getItemClientePorContrato,
  setIdClientePorContrato,
  setClearItemClientePorContrato,
  setIsLoading,
  setStatus,
  setResult,
  setSuccess,
  setMensaje,
} = itemClientePorContratoSlice.actions;

export default itemClientePorContratoSlice.reducer;
